import { useMemo, useState } from "react";
import { ExportMenu } from "../components/ExportMenu";

function dateOnly(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString();
}

function durationMinutes(startValue, endValue) {
  const start = new Date(startValue || "");
  const end = new Date(endValue || "");
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return "";
  }
  return Math.round((end - start) / 60000);
}

function cleanLabel(value = "") {
  return String(value || "")
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function csvCell(value) {
  const text = value == null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, headers, rows) {
  const body = [
    headers.map((header) => csvCell(header.label)).join(","),
    ...rows.map((row) => headers.map((header) => csvCell(header.value(row))).join(","))
  ].join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function recordDateValue(item = {}) {
  return item.startsAt || item.startAt || item.paidAt || item.createdAt || item.updatedAt;
}

function inRange(item, startDate, endDate) {
  const value = dateOnly(recordDateValue(item));
  if (!value) {
    return true;
  }
  return (!startDate || value >= startDate) && (!endDate || value <= endDate);
}

function contactEmail(record = {}) {
  return record.email ||
    record.contactEmail ||
    record.customerEmail ||
    record.memberSnapshot?.email ||
    record.customer?.email ||
    "";
}

function reservationContactName(reservation = {}, memberNameById = new Map()) {
  return reservation.memberName ||
    reservation.customerName ||
    reservation.memberSnapshot?.fullName ||
    reservation.customer?.name ||
    memberNameById.get(reservation.memberId) ||
    "Member";
}

function locationLabel(value) {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  return [
    value.name,
    value.label,
    value.area,
    value.addressLine1,
    value.city
  ].filter(Boolean).join(", ");
}

function eventInstructor(event = {}) {
  return event.instructorName ||
    event.instructor?.fullName ||
    event.leadInstructor?.fullName ||
    event.chapterSnapshot?.leader?.fullName ||
    event.ownerName ||
    "";
}

function eventChapter(event = {}) {
  return event.chapterName ||
    event.chapterSnapshot?.name ||
    event.chapter?.name ||
    "";
}

function eventRegistrationCount(event = {}) {
  return event.registrationCount ??
    event.registrationsCount ??
    event.registrationSummary?.confirmed ??
    event.rosterSummary?.registered ??
    "";
}

function isNonPublicMemberRecord(member = {}) {
  const email = String(member.email || "").toLowerCase();
  const name = String(member.fullName || "").toLowerCase();
  const source = String(member.source || "").toLowerCase();
  return /(?:@dev\.rangegrid\.local|rangegrid-.*audit|owner-audit|test@|example\.com)/i.test(email)
    || /(?:demo|audit|test)/i.test(name)
    || /(?:codex|audit|test)/i.test(source);
}

export function ReportsPage({
  events = [],
  loading,
  members = [],
  reservations = [],
  resources = []
}) {
  const today = new Date();
  const prior = new Date(today);
  prior.setDate(prior.getDate() - 30);
  const [filters, setFilters] = useState({
    startDate: prior.toISOString().slice(0, 10),
    endDate: today.toISOString().slice(0, 10)
  });

  const filteredReservations = useMemo(() => reservations.filter((item) => inRange(item, filters.startDate, filters.endDate)), [reservations, filters.endDate, filters.startDate]);
  const filteredEvents = useMemo(() => events.filter((item) => inRange(item, filters.startDate, filters.endDate)), [events, filters.endDate, filters.startDate]);
  const visibleMembers = useMemo(() => members.filter((member) => !isNonPublicMemberRecord(member)), [members]);
  const resourceById = useMemo(() => new Map(resources.map((resource) => [resource.resourceId, resource])), [resources]);
  const resourceNameById = useMemo(() => new Map(resources.map((resource) => [resource.resourceId, resource.name || resource.label || "Range resource"])), [resources]);
  const memberNameById = useMemo(() => new Map(visibleMembers.map((member) => [member.memberId, member.fullName || member.email || "Member"])), [visibleMembers]);

  const activeMembers = visibleMembers.filter((member) => member.status === "active").length;
  const pendingMembers = visibleMembers.filter((member) => ["pending", "review"].includes(member.status) || member.review?.status === "pending").length;
  const upcomingEvents = filteredEvents.filter((event) => new Date(event.startsAt || event.startAt || 0) >= new Date()).length;
  const activeReservations = filteredReservations.filter((reservation) => reservation.status !== "cancelled").length;
  const utilization = resources.length > 0
    ? Math.round((activeReservations / Math.max(resources.length * 30, 1)) * 100)
    : 0;

  const lines = [
    {
      label: "Membership",
      count: activeMembers + pendingMembers,
      detail: `${activeMembers} active / ${pendingMembers} pending review`
    },
    {
      label: "Range bookings",
      count: activeReservations,
      detail: `${filteredReservations.length} total reservations in range`
    },
    {
      label: "Programs",
      count: filteredEvents.length,
      detail: `${upcomingEvents} upcoming`
    }
  ];

  function exportMembersReport() {
    downloadCsv("rangegrid-members-report.csv", [
      { label: "Name", value: (member) => member.fullName || member.email || "Member" },
      { label: "Email", value: (member) => member.email || "" },
      { label: "Phone", value: (member) => member.phone || "" },
      { label: "Status", value: (member) => cleanLabel(member.effectiveStatus || member.status || "") },
      { label: "Review", value: (member) => cleanLabel(member.review?.status || member.reviewStatus || "") },
      { label: "Waiver", value: (member) => cleanLabel(member.reviewSummary?.waiverStatus || member.waiverStatus || "") },
      { label: "Member since", value: (member) => dateOnly(member.currentTermStart || member.createdAt) },
      { label: "Renewal", value: (member) => dateOnly(member.currentTermEnd) }
    ], visibleMembers);
  }

  function exportReservationsReport() {
    downloadCsv("rangegrid-reservations-report.csv", [
      { label: "Member", value: (reservation) => reservationContactName(reservation, memberNameById) },
      { label: "Email", value: (reservation) => contactEmail(reservation) },
      { label: "Resource", value: (reservation) => reservation.resourceName || resourceNameById.get(reservation.resourceId) || "Range resource" },
      { label: "Resource type", value: (reservation) => cleanLabel(resourceById.get(reservation.resourceId)?.type || resourceById.get(reservation.resourceId)?.category || "") },
      { label: "Starts", value: (reservation) => formatDateTime(reservation.startsAt || reservation.startAt) },
      { label: "Ends", value: (reservation) => formatDateTime(reservation.endsAt || reservation.endAt) },
      { label: "Duration minutes", value: (reservation) => durationMinutes(reservation.startsAt || reservation.startAt, reservation.endsAt || reservation.endAt) },
      { label: "Status", value: (reservation) => cleanLabel(reservation.status || "") },
      { label: "Attendance", value: (reservation) => cleanLabel(reservation.attendance?.status || reservation.checkInStatus || "") },
      { label: "Source", value: (reservation) => cleanLabel(reservation.source || "") },
      { label: "Purpose", value: (reservation) => reservation.purpose || reservation.visitType || "" },
      { label: "Created", value: (reservation) => formatDateTime(reservation.createdAt) }
    ], filteredReservations);
  }

  function exportEventsReport() {
    downloadCsv("rangegrid-programs-report.csv", [
      { label: "Program", value: (event) => event.title || event.name || "Program" },
      { label: "Starts", value: (event) => formatDateTime(event.startsAt || event.startAt) },
      { label: "Ends", value: (event) => formatDateTime(event.endsAt || event.endAt) },
      { label: "Location", value: (event) => event.locationName || locationLabel(event.location) || event.resourceName || "Range" },
      { label: "Chapter", value: (event) => eventChapter(event) },
      { label: "Instructor", value: (event) => eventInstructor(event) },
      { label: "Type", value: (event) => cleanLabel(event.type || "") },
      { label: "Status", value: (event) => cleanLabel(event.status || "") },
      { label: "Publish status", value: (event) => cleanLabel(event.registrationStatus || event.publishStatus || event.visibility || "") },
      { label: "Capacity", value: (event) => event.capacity || "" },
      { label: "Registered", value: (event) => eventRegistrationCount(event) },
      { label: "Approval", value: (event) => cleanLabel(event.approval?.status || "") },
      { label: "Created", value: (event) => formatDateTime(event.createdAt) }
    ], filteredEvents);
  }

  return (
    <div className="reports-page">
      {loading && <div className="status-strip">Loading reports...</div>}
      <section className="panel">
        <div className="panel-header report-toolbar">
          <div>
            <h2>Operational reports</h2>
            <p>Use quick counts and CSV exports for membership, booking, and program review. Trends stay in Insights; payment setup stays in Payments.</p>
          </div>
          <div className="inline-fields">
            <label>
              Start
              <input type="date" value={filters.startDate} onChange={(event) => setFilters((current) => ({ ...current, startDate: event.target.value }))} />
            </label>
            <label>
              End
              <input type="date" value={filters.endDate} onChange={(event) => setFilters((current) => ({ ...current, endDate: event.target.value }))} />
            </label>
          </div>
        </div>
      </section>

      <section className="module-metrics">
        <div>
          <span>Active members</span>
          <strong>{activeMembers}</strong>
        </div>
        <div>
          <span>Pending reviews</span>
          <strong>{pendingMembers}</strong>
        </div>
        <div>
          <span>Upcoming programs</span>
          <strong>{upcomingEvents}</strong>
        </div>
        <div>
          <span>Resource utilization</span>
          <strong>{utilization}%</strong>
        </div>
      </section>

      <div className="report-grid">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Activity summary</h2>
              <p>Operational volume for the selected date range.</p>
            </div>
          </div>
          <div className="table-like compact-table">
            <div className="table-row table-head">
              <span>Area</span>
              <span>Records</span>
              <span>Owner check</span>
            </div>
            {lines.map((line) => (
              <div className="table-row" key={line.label}>
                <span>{line.label}</span>
                <span>{line.count}</span>
                <span>{line.detail}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Follow-up checks</h2>
              <p>Simple counts that help spot owner follow-up.</p>
            </div>
          </div>
          <dl className="ops-facts">
            <div>
              <dt>Pending members</dt>
              <dd>{pendingMembers}</dd>
            </div>
            <div>
              <dt>Upcoming events</dt>
              <dd>{upcomingEvents}</dd>
            </div>
            <div>
              <dt>Cancelled reservations</dt>
              <dd>{filteredReservations.filter((reservation) => reservation.status === "cancelled").length}</dd>
            </div>
            <div>
              <dt>Facility records</dt>
              <dd>{resources.length}</dd>
            </div>
          </dl>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Export operational data</h2>
              <p>Choose the operational dataset you want to review outside RangeGrid.</p>
            </div>
          </div>
          <ExportMenu
            buttonLabel="Export report data"
            items={[
              {
                key: "members",
                label: "Members",
                description: "Member roster with contact, status, review, waiver, and renewal fields.",
                onExport: exportMembersReport
              },
              {
                key: "reservations",
                label: "Reservations",
                description: "Lane, bay, classroom, and range reservation records.",
                onExport: exportReservationsReport
              },
              {
                key: "events",
                label: "Events",
                description: "Event setup and schedule records.",
                onExport: exportEventsReport
              }
            ]}
            title="Choose export"
          />
        </section>
      </div>
    </div>
  );
}
